/**
 * Phase 86 Plan 86-05 Task 2 — useCloseTasks.
 *
 * Resolves each task's OWN project's terminal state and dispatches the
 * close through the existing state-change endpoint (`patchTaskState`, the
 * same mutation `useTaskMutation.ts` uses for the board). Never a
 * hard-coded state id: "the row already carries its project's state
 * colour" precisely because the state palette is per-project
 * (README "REVIVE" + 86-CONTEXT "Row actions").
 *
 * One hook backs BOTH the REVIVE menu's single-task "Close as won't do"
 * item and the graveyard band's BULK CLOSE dialog — one resolution rule, so
 * a project with no terminal state fails the identical way from either
 * surface, by name, never a silent no-op (T-86-16 / T-86-18).
 *
 * Terminal-state resolution mirrors the backend's own two-tier rule:
 *   - `taskAuthorReviewService.ts`'s reopen-target lookup prefers the
 *     project's own `isTerminal`-flagged state (Phase 20, per-project,
 *     editable in the Task States list / board column header).
 *   - Falls back to matching the state's NAME against the same four legacy
 *     names `taskAssigneeService.ts`'s `TERMINAL_TASK_STATE_NAMES` /
 *     `taskAuthorReviewService.ts`'s own local copy already treat as
 *     terminal — the newer flag is opt-in and many projects never set it.
 * A project with neither surfaces a failure naming that project — never a
 * silent skip.
 *
 * T-86-17 (denial of service) — every task in a call is dispatched
 * sequentially, one `patchTaskState` request at a time, never fanned out
 * concurrently; a project's state list is fetched once (via the same
 * `['projectStates', projectId]` cache key `useProjectStates` already uses)
 * and reused across every task on that project in the same call.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getProjectStates, type TaskState } from '../services/api/projectStates';
import { patchTaskState } from '../services/api/board';
import { taskActivityQueryKey } from './useTaskActivity';
import { MY_TASKS_BASE_KEY } from './useMyTasks';

/**
 * Mirrors `taskAssigneeService.ts`'s `TERMINAL_TASK_STATE_NAMES` /
 * `taskAuthorReviewService.ts`'s own local copy of the same list (that file's
 * docstring: "`TaskState.isTerminal` is a newer per-project flag many
 * projects never set, so the legacy name list stays the backstop"). Kept as
 * a second, explicitly-commented copy on the frontend — same repo
 * convention as that backend pair — rather than a cross-package import,
 * since no `@shared` constant for this exists today.
 */
const TERMINAL_TASK_STATE_NAMES: readonly string[] = ['Done', 'Archived', 'Closed', 'Deleted'];

/** Exported for unit testing in isolation from the mutation/network shell. */
export function resolveTerminalState(states: TaskState[]): TaskState | null {
  return (
    states.find((s) => s.isTerminal === true) ??
    states.find((s) => TERMINAL_TASK_STATE_NAMES.includes(s.name)) ??
    null
  );
}

export interface CloseTaskTarget {
  taskId: number;
  projectId: number;
  projectName: string;
}

export interface CloseTaskFailure {
  taskId: number;
  projectName: string;
  reason: string;
}

export interface CloseTasksResult {
  succeededTaskIds: number[];
  failures: CloseTaskFailure[];
}

export function useCloseTasks() {
  const queryClient = useQueryClient();

  return useMutation<CloseTasksResult, Error, CloseTaskTarget[]>({
    mutationFn: async (targets) => {
      const statesByProject = new Map<number, TaskState[]>();
      const succeededTaskIds: number[] = [];
      const failures: CloseTaskFailure[] = [];

      // Sequential, bounded dispatch (T-86-17) — never Promise.all across
      // targets. One state-list fetch per DISTINCT project, reused for every
      // task on that project in this same call.
      for (const target of targets) {
        const cached = statesByProject.get(target.projectId);
        let states: TaskState[] | null = cached ?? null;
        if (!states) {
          try {
            const fetched = await queryClient.fetchQuery({
              queryKey: ['projectStates', target.projectId],
              queryFn: () => getProjectStates(target.projectId),
            });
            statesByProject.set(target.projectId, fetched);
            states = fetched;
          } catch (err) {
            failures.push({
              taskId: target.taskId,
              projectName: target.projectName,
              reason:
                err instanceof Error
                  ? err.message
                  : `Could not load ${target.projectName}'s task states`,
            });
            continue;
          }
        }

        const terminal = resolveTerminalState(states);
        if (!terminal) {
          // T-86-16 / T-86-18 — never a silent skip. Named by project so a
          // bulk close spanning several projects tells the user exactly
          // which one is missing configuration.
          failures.push({
            taskId: target.taskId,
            projectName: target.projectName,
            reason: `${target.projectName} has no terminal (Done/Closed) state configured`,
          });
          continue;
        }

        try {
          await patchTaskState(target.taskId, terminal.id);
          succeededTaskIds.push(target.taskId);
        } catch (err) {
          failures.push({
            taskId: target.taskId,
            projectName: target.projectName,
            reason: err instanceof Error ? err.message : `Failed to close this task`,
          });
        }
      }

      return { succeededTaskIds, failures };
    },
    onSettled: (data) => {
      // Invalidate rather than optimistically remove — a partial failure
      // must leave its survivors visibly unresolved, not optimistically
      // vanish (behavior spec: "the successes stand and the failures are
      // reported by name"). The my-tasks list + board caches both read
      // taskState/isTerminal off the server, same as the existing
      // `useTaskMutation` state-change mutation invalidates.
      queryClient.invalidateQueries({ queryKey: MY_TASKS_BASE_KEY });
      queryClient.invalidateQueries({ queryKey: ['board'] });
      for (const taskId of data?.succeededTaskIds ?? []) {
        queryClient.invalidateQueries({ queryKey: taskActivityQueryKey(taskId) });
      }
    },
  });
}
